// Shared error toaster with "Copy" + "Report bug" actions so users can
// either grab the upstream message for a bug report or jump straight into
// the feedback form with the error pre-filled. Use this anywhere we surface
// a backend / AI gateway error to the user.
import { toast } from "sonner";
import { formatDebugContext } from "./debugCapture";
import { supabase } from "@/integrations/supabase/client";

const REPORT_KEY = "hackerlab.bugReport.draft";

export type ReportDraft = {
  /** Short summary used as the Feedback page subject. */
  summary: string;
  /** Full upstream error body. */
  details: string;
  /** Where the error happened (window.location.href at the time). */
  pageUrl: string;
  /** ISO timestamp captured at toast creation. */
  capturedAt: string;
  /** Auto-captured repro context: viewport + recent console errors. */
  debugContext?: string;
};

/**
 * Read + clear the bug report draft saved by toastError. Called by the
 * Feedback page on mount to prefill the form. Returns null if no draft
 * exists or if it's older than 10 minutes (stale).
 */
export function consumeReportDraft(): ReportDraft | null {
  try {
    const raw = sessionStorage.getItem(REPORT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(REPORT_KEY);
    const draft = JSON.parse(raw) as ReportDraft;
    const age = Date.now() - new Date(draft.capturedAt).getTime();
    if (age > 10 * 60 * 1000) return null; // 10-minute freshness window
    return draft;
  } catch {
    return null;
  }
}

export function toastError(
  message: string,
  opts?: { title?: string; durationMs?: number; source?: "runtime" | "edge_function" | "manual"; edgeFunction?: string },
) {
  const title = opts?.title ?? "Something went wrong";
  const id = toast.error(title, {
    description: (
      <div className="flex flex-col gap-2">
        <p className="text-xs opacity-90 break-words">{message}</p>
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => copyToClipboard(message)}
            className="text-[11px] font-mono px-2 py-1 rounded border border-border bg-background/50 hover:bg-background"
          >
            Copy
          </button>
          <button
            type="button"
            onClick={() => submitReport(title, message, id, opts?.source ?? "manual", opts?.edgeFunction)}
            className="text-[11px] font-mono px-2 py-1 rounded border border-primary/40 bg-primary/10 text-primary hover:bg-primary/15"
          >
            Report bug
          </button>
        </div>
      </div>
    ),
    duration: opts?.durationMs ?? 12_000,
  });
  return id;
}

function copyToClipboard(text: string) {
  const done = () => toast.success("Error copied to clipboard");
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => fallbackCopy(text, done));
    } else {
      fallbackCopy(text, done);
    }
  } catch {
    fallbackCopy(text, done);
  }
}

function fallbackCopy(text: string, onDone: () => void) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    onDone();
  } catch {
    toast.error("Could not copy automatically — please select and copy manually.");
  }
}

function openReport(title: string, details: string, toastId: string | number) {
  // Stash the prefill payload in sessionStorage so the Feedback page can
  // read it on mount without us having to thread state through the router
  // or push a giant query string.
  try {
    const draft: ReportDraft = {
      summary: title,
      details,
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      capturedAt: new Date().toISOString(),
      debugContext: formatDebugContext(),
    };
    sessionStorage.setItem(REPORT_KEY, JSON.stringify(draft));
  } catch {
    // sessionStorage can throw in private mode — we still navigate so the
    // user can manually paste, falling back gracefully.
  }
  toast.dismiss(toastId);
  // SPA navigation isn't available here without coupling to react-router,
  // so use a same-origin redirect. The Feedback page reads the draft on mount.
  if (typeof window !== "undefined") {
    window.location.href = "/feedback?prefill=1";
  }
}

/**
 * One-click bug submission. Pushes the error into the `bug_reports`
 * queue via the `report-bug` edge function. A nightly cron job triages
 * the queue with AI and emails the digest to admins.
 */
export async function submitReport(
  title: string,
  details: string,
  toastId: string | number,
  source: "runtime" | "edge_function" | "manual" = "manual",
  edgeFunction?: string,
) {
  toast.dismiss(toastId);
  const pending = toast.loading("Filing bug report…");
  try {
    const { data, error } = await supabase.functions.invoke("report-bug", {
      body: {
        title,
        description: details,
        source,
        edgeFunction,
        url: typeof window !== "undefined" ? window.location.href : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        debugContext: formatDebugContext(),
      },
    });
    if (error || (data as any)?.error) throw new Error((error?.message ?? (data as any)?.error) || "report failed");
    toast.dismiss(pending);
    toast.success("Bug reported", {
      description: "Triaged tonight by AI. Thanks for helping us fix this.",
      duration: 6_000,
    });
  } catch (e: any) {
    toast.dismiss(pending);
    toast.error("Couldn't file report", {
      description: e?.message ?? "Please try again in a moment.",
    });
  }
}
